import React, { FC } from 'react';
import styled from '@tanium/react-emotion-9';
import { SimpleTooltip } from '@tanium/react-tooltip';

interface DisabledProps {
  disabled?: boolean;
}

type SimpleTooltipProps = React.ComponentPropsWithoutRef<typeof SimpleTooltip>;

export interface TooltippedButtonWrapperProps extends DisabledProps {
  /**
   * Content to appear in the tooltip.
   */
  tooltipContent?: SimpleTooltipProps['content'];
  /**
   * Modifiers for the tooltip based on Popper's modifiers structure.
   */
  tooltipModifiers?: SimpleTooltipProps['modifiers'];
  /**
   * Placement of the tooltip. Follows popper's conventions.
   */
  tooltipPlacement?: SimpleTooltipProps['placement'];
  /**
   * The component that will receive the tooltip.
   */
  children: React.ReactNode;
}

/**
 * Wrapper that shows 'not-allowed' cursor when disabled=true.
 */
const DisabledCursorWrapper = styled('div', {
  shouldForwardProp: (prop) => !['disabled', 'innerRef'].includes(prop),
})(
  {
    display: 'inline-flex', // ensure this wrapper shrinks to the size of the content
  },
  ({ disabled }: DisabledProps) => ({
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
);

/**
 * Wrapper that disallows pointer events when disabled=true.
 */
const DisabledPointerEventsWrapper = styled('span', {
  shouldForwardProp: (prop) => !['disabled', 'innerRef'].includes(prop),
})(
  {
    display: 'inline-flex', // ensure this wrapper shrinks to the size of the content
  },
  ({ disabled }: DisabledProps) => ({
    pointerEvents: disabled ? 'none' : 'auto',
  }),
);

/**
 * Internal component that can wrap Button-like components with tooltip functionality
 * that still functions even when the component is disabled.
 */
const TooltippedButtonWrapperInner = ({
  disabled,
  tooltipContent,
  tooltipModifiers,
  tooltipPlacement,
  children,
}: TooltippedButtonWrapperProps) => (
  <SimpleTooltip content={tooltipContent} modifiers={tooltipModifiers} placement={tooltipPlacement}>
    {({ ref }) => (
      <DisabledCursorWrapper innerRef={ref} disabled={disabled}>
        <DisabledPointerEventsWrapper disabled={disabled}>{children}</DisabledPointerEventsWrapper>
      </DisabledCursorWrapper>
    )}
  </SimpleTooltip>
);

// This type cast provides better typings and Intellisense for consumers
export const TooltippedButtonWrapper = TooltippedButtonWrapperInner as FC<
  TooltippedButtonWrapperProps & { ref?: React.Ref<HTMLElement> }
>;
